import { useToaster } from '@europrocurement/l2d-modules/hooks';
import { FlexyButton, PriceLigne } from '@europrocurement/flexy-components';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { PrestationArticlesJsonldPrestationrelationUpdate } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { deleteIcon } from '@europrocurement/l2d-icons';
import { Box, Divider } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { calculTotalHt, calculTotalTtc, calculTotalTva } from '@europrocurement/l2d-utils';
import { ArticlePrefacturation } from '@europrocurement/l2d-modules/providers';

export type ArticleUpdateProps = {
    article: ArticlePrefacturation;
    onUpdate?: (article: ArticlePrefacturation) => void;
    onDelete?: (articleId: number) => void;
    afterSubmit?: () => void;
};

const ArticleUpdate: React.FunctionComponent<ArticleUpdateProps> = function (props) {
    const { prestationArticlesApi } = useFormaliteService();
    const { article, onUpdate, onDelete, afterSubmit } = props;

    const { handleApiResponse } = useToaster();
    const formStructure: FormStructure[] = [
        {
            type: 'header',
            label: 'Éditer une prestation',
            name: 'main_informations',
        },
        {
            type: 'text',
            name: 'libelle',
            inputlabel: 'Libellé',
            rules: {
                required: 'Vous devez renseigner un libellé',
            },
            placeholder: 'Libellé de la prestation',
            // defaultValue: article.libelle,
            xs: 12,
            sm: 10,
            md: 10,
            lg: 10,
        },
        {
            type: 'boolean',
            name: 'bold',
            inputlabel: 'Libellé en gras',
            verticalSwitch: true,
            xs: 12,
            sm: 2,
            md: 2,
            lg: 2,
        },
    ];
    const formStructureConditioned: FormStructure[] = [
        {
            type: 'number',
            inputProps: {
                step: 1,
                min: 0,
            },
            name: 'ht',
            inputlabel: 'Prix unitaire',
            placeholder: 'Prix unitaire',
            rules: {
                required: 'Vous devez reseigner un prix',
            },
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                if (Number(e.target.value) === 0) {
                    // eslint-disable-next-line no-param-reassign
                    e.target.value = '';
                }
            },
            xs: 12,
            sm: 6,
            md: 4,
            lg: 4,
        },
        {
            type: 'number',
            inputProps: {
                step: 1,
                min: 0,
            },
            name: 'qte',
            inputlabel: 'Quantité',
            placeholder: 'Quantité',
            rules: {
                required: 'Vous devez reseigner une quantité',
            },
            xs: 12,
            sm: 6,
            md: 4,
            lg: 4,
            // defaultValue: quantite,
        },
        {
            type: 'number',
            disabled: true,
            inputlabel: '% TVA',
            name: 'tva',
            xs: 12,
            sm: 6,
            md: 4,
            lg: 4,
            rules: {
                required: 'Vous devez reseigner une Tva',
            },
            defaultValue: article.tauxTva,
        },
    ];
    if (article.rubFac !== 'LIB') {
        formStructure.push(...formStructureConditioned);
    }

    const formContext = useForm({
        mode: 'onTouched',
        defaultValues: {
            qte: article.quantite,
            ht: parseFloat(Number(article.prixBase).toFixed(4)),
            libelle: article.libelle,
            bold: article.bold,
        },
    });

    const onSubmit = async () => {
        if (!article.prestationId) {
            throw new Error('missing prestation id');
        }
        let bold = false;
        let tarif = '0';
        let quantite = 0;
        const libelle = formContext.getValues(`libelle`) as unknown as string;
        bold = formContext.getValues().bold;
        if (article.rubFac !== 'LIB') {
            tarif = formContext.getValues().ht === 0 ? '0' : String(formContext.getValues().ht);
            quantite = Number(formContext.getValues().qte as unknown as number);
            // formContext.getValues(`qte`) as unknown as number
        }
        const prestationArticlesJsonldPrestationrelationUpdate: PrestationArticlesJsonldPrestationrelationUpdate =
            {
                idPresta: article.prestationId as unknown as string,
                libelle,
                quantite,
                rang: article.rang,
                tarif,
                bold,
            };

        const updateRequest = prestationArticlesApi.apiArticlesPrestationIdPut({
            id: article.id as unknown as string,
            prestationArticlesJsonldPrestationrelationUpdate,
        });

        if (onUpdate) {
            onUpdate({ ...article, quantite, libelle, prixBase: tarif });
        }

        await handleApiResponse(updateRequest, afterSubmit);
    };
    const formProps = {
        formContext,
        onSubmit,
        formObject: {},
        submitButton: {
            displayed: false,
        },
    };

    const deleteArticle = async () => {
        if (onDelete) {
            onDelete(article.id);
        }
    };

    formContext.watch(['ht']);
    formContext.watch(['qte']);

    return (
        <>
            <FlexyForm
                formStructure={formStructure}
                {...formProps}
            />
            {article.rubFac !== 'LIB' ? (
                <>
                    <Divider
                        sx={{
                            marginTop: '10px',
                        }}
                    />

                    <PriceLigne
                        label="Total HT"
                        price={`${calculTotalHt(formContext.getValues(`ht`), formContext.getValues(`qte`)).toFixed(2)} €`}
                        sx={{
                            marginTop: '10px',
                        }}
                    />
                    <PriceLigne
                        label=" TVA"
                        price={`${calculTotalTva(formContext.getValues(`ht`), formContext.getValues(`qte`), article.tauxTva).toFixed(2)} €`}
                    />
                    <PriceLigne
                        label=" Total TTC"
                        price={`${calculTotalTtc(formContext.getValues(`ht`), formContext.getValues(`qte`), article.tauxTva).toFixed(2)} €`}
                    />
                </>
            ) : null}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '32px',
                    marginTop: '10px',
                }}
            >
                {article.articleSupprimable ? (
                    <FlexyButton
                        variant="text"
                        sx={{ padding: '16px 24px' }}
                        icon={deleteIcon}
                        label="Supprimer la prestation"
                        color="error"
                        onClick={deleteArticle}
                    />
                ) : null}
                <FlexyButton
                    variant="contained"
                    sx={{ padding: '16px 24px' }}
                    label="Valider"
                    onClick={onSubmit}
                />
            </Box>
        </>
    );
};

export default ArticleUpdate;
